import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Clock, CalendarOff, Plus, Trash2, X, AlertCircle } from 'lucide-react';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = {Mon:'monday',Tue:'tuesday',Wed:'wednesday',Thu:'thursday',Fri:'friday',Sat:'saturday',Sun:'sunday'};

const NOTICE_OPTIONS = [
  { label: 'Same day (no minimum)', value: 0 },
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '3 days', value: 72 },
  { label: '1 week', value: 168 },
  { label: 'Custom', value: 'custom' },
];

export default function AvailabilityTab() {
  const [schedule, setSchedule] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [minNoticeHours, setMinNoticeHours] = useState(0);
  const [customNotice, setCustomNotice] = useState('');
  const [isCustomNotice, setIsCustomNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({ date: '', reason: '', all_day: true, start_time: '', end_time: '' });
  const [peakPricing, setPeakPricing] = useState({ enabled: false, days: [], type: 'fixed', amount: 0 });

  useEffect(() => { fetchAvailability(); }, []);

  const fetchAvailability = async () => {
    try {
      const res = await api.get('/providers/me/availability');
      setSchedule(res.data.weekly_schedule);
      setBlockedDates(res.data.blocked_dates || []);
      const pp = res.data.peak_pricing || { enabled: false, days: [], type: 'fixed', amount: 0 };
      setPeakPricing(pp);
      const hours = res.data.min_notice_hours || 0;
      const isPreset = NOTICE_OPTIONS.some(o => o.value === hours);
      if (isPreset) {
        setMinNoticeHours(hours);
        setIsCustomNotice(false);
      } else {
        setMinNoticeHours('custom');
        setCustomNotice(String(hours));
        setIsCustomNotice(true);
      }
    } catch (err) { console.error('Load availability error:', err); toast.error('Failed to load availability'); }
    finally { setLoading(false); }
  };

  const getNoticeValue = () => {
    if (isCustomNotice || minNoticeHours === 'custom') {
      return parseInt(customNotice) || 0;
    }
    return minNoticeHours;
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await api.put('/providers/me/availability', {
        weekly_schedule: schedule,
        min_notice_hours: getNoticeValue(),
        peak_pricing: peakPricing,
      });
      toast.success('Schedule saved');
    } catch (err) { console.error('Save schedule error:', err); toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addBlock = async () => {
    if (!blockForm.date) { toast.error('Please select a date'); return; }
    try {
      const res = await api.post('/providers/me/availability/block', blockForm);
      setBlockedDates([res.data, ...blockedDates]);
      setBlockForm({ date: '', reason: '', all_day: true, start_time: '', end_time: '' });
      setShowBlockForm(false);
      toast.success('Date blocked');
    } catch { toast.error('Failed to block date'); }
  };

  const removeBlock = async (blockId) => {
    try {
      await api.delete(`/providers/me/availability/block/${blockId}`);
      setBlockedDates(blockedDates.filter(b => b.block_id !== blockId));
      toast.success('Block removed');
    } catch (err) { console.error('Remove block error:', err); toast.error('Failed to remove block'); }
  };

  const updateDay = (day, field, value) => {
    setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" /></div>;

  return (
    <div className="space-y-6" data-testid="availability-tab">
      {/* Weekly Schedule */}
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#D4AF37]" />
            Weekly Hours
          </h3>
          <Button onClick={saveSchedule} disabled={saving} className="btn-primary" data-testid="save-schedule-btn">
            {saving ? 'Saving...' : 'Save Schedule'}
          </Button>
        </div>

        <div className="space-y-3">
          {DAYS.map(day => {
            const d = schedule?.[day] || { enabled: false, start: '09:00', end: '18:00' };
            const label = Object.entries(DAY_LABELS).find(([,v]) => v === day)?.[0] || day;
            return (
              <div
                key={day}
                className={`flex flex-wrap items-center gap-3 p-3 rounded-lg ${
                  d.enabled ? 'bg-white border border-[#E6E4DD]' : 'bg-stone-50 border border-stone-100'
                }`}
                data-testid={`day-${day}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Switch
                    checked={d.enabled}
                    onCheckedChange={(v) => updateDay(day, 'enabled', v)}
                    data-testid={`toggle-${day}`}
                  />
                  <span className={`w-12 text-sm font-medium ${d.enabled ? 'text-[#1A1A1A]' : 'text-stone-400'}`}>
                    {label}
                  </span>
                </div>
                {d.enabled ? (
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:flex-1">
                    <Input
                      type="time"
                      value={d.start}
                      onChange={e => updateDay(day, 'start', e.target.value)}
                      className="input-zuriya w-[110px] sm:w-32"
                      data-testid={`start-${day}`}
                    />
                    <span className="text-stone-400 text-sm">to</span>
                    <Input
                      type="time"
                      value={d.end}
                      onChange={e => updateDay(day, 'end', e.target.value)}
                      className="input-zuriya w-[110px] sm:w-32"
                      data-testid={`end-${day}`}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-stone-400 italic">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Minimum Notice Period */}
      <div className="card-surface p-4 sm:p-6" data-testid="notice-period-section">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2 mb-2">
          <AlertCircle className="h-5 w-5 text-[#D4AF37]" />
          Minimum Booking Notice
        </h3>
        <p className="text-sm text-stone-500 mb-4">How much advance notice do you need before a client can book?</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {NOTICE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                setMinNoticeHours(opt.value);
                setIsCustomNotice(opt.value === 'custom');
                if (opt.value !== 'custom') setCustomNotice('');
              }}
              className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors border ${
                (opt.value === 'custom' ? isCustomNotice : minNoticeHours === opt.value)
                  ? 'bg-[#D4AF37] text-white border-[#D4AF37]'
                  : 'bg-white text-[#1A1A1A] border-[#E6E4DD] hover:border-[#D4AF37]'
              }`}
              data-testid={`notice-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {isCustomNotice && (
          <div className="mt-3 flex items-center gap-2">
            <Input
              type="number"
              min="1"
              value={customNotice}
              onChange={e => setCustomNotice(e.target.value)}
              placeholder="e.g. 12"
              className="input-zuriya w-28"
              data-testid="custom-notice-input"
            />
            <span className="text-sm text-stone-600">hours</span>
          </div>
        )}

        <p className="text-xs text-stone-400 mt-3">
          {getNoticeValue() === 0
            ? 'Clients can book same-day appointments.'
            : `Clients must book at least ${getNoticeValue()} hour${getNoticeValue() > 1 ? 's' : ''} in advance.`}
        </p>
      </div>

      {/* Peak Pricing */}
      <div className="card-surface p-4 sm:p-6" data-testid="peak-pricing-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#D4AF37]" />
            Peak Pricing
          </h3>
          <Switch
            checked={peakPricing.enabled}
            onCheckedChange={v => setPeakPricing(prev => ({ ...prev, enabled: v }))}
            data-testid="peak-pricing-toggle"
          />
        </div>
        <p className="text-sm text-stone-500 mb-4">Charge more on busy days. The surcharge is added to each service price automatically.</p>

        {peakPricing.enabled && (
          <div className="space-y-4 animate-fadeIn">
            <div>
              <Label className="text-sm font-medium text-[#1A1A1A] mb-2 block">Peak days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map(day => {
                  const isSelected = peakPricing.days.includes(day);
                  return (
                    <button
                      key={day}
                      onClick={() => setPeakPricing(prev => ({
                        ...prev,
                        days: isSelected ? prev.days.filter(d => d !== day) : [...prev.days, day]
                      }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                        isSelected
                          ? 'bg-[#D4AF37] text-white'
                          : 'bg-[#F9F6F0] text-stone-600 hover:border-[#D4AF37] border border-[#E6E4DD]'
                      }`}
                      data-testid={`peak-day-${day}`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1 block">Surcharge type</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPeakPricing(prev => ({ ...prev, type: 'fixed' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      peakPricing.type === 'fixed' ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white border-[#E6E4DD]'
                    }`}
                    data-testid="peak-type-fixed"
                  >
                    Fixed (£)
                  </button>
                  <button
                    onClick={() => setPeakPricing(prev => ({ ...prev, type: 'percentage' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      peakPricing.type === 'percentage' ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white border-[#E6E4DD]'
                    }`}
                    data-testid="peak-type-percentage"
                  >
                    Percentage (%)
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Amount</Label>
                <Input
                  type="number"
                  min="0"
                  value={peakPricing.amount}
                  onChange={e => setPeakPricing(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  placeholder={peakPricing.type === 'fixed' ? 'e.g. 10' : 'e.g. 15'}
                  className="input-zuriya"
                  data-testid="peak-amount-input"
                />
              </div>
            </div>

            {peakPricing.days.length > 0 && peakPricing.amount > 0 && (
              <p className="text-xs text-stone-500 bg-[#F9F6F0] p-3 rounded-lg">
                On <span className="font-medium capitalize">{peakPricing.days.map(d => d.slice(0, 3)).join(', ')}</span>,
                each service will cost{' '}
                <span className="font-semibold text-[#D4AF37]">
                  {peakPricing.type === 'fixed' ? `+£${peakPricing.amount}` : `+${peakPricing.amount}%`}
                </span>
                {' '}more.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Blocked Dates */}
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <CalendarOff className="h-5 w-5 text-[#D4AF37]" />
            Blocked Dates
          </h3>
          <Button variant="outline" onClick={() => setShowBlockForm(!showBlockForm)} className="text-sm" data-testid="add-block-btn">
            {showBlockForm ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            {showBlockForm ? 'Cancel' : 'Block a Date'}
          </Button>
        </div>

        {showBlockForm && (
          <div className="mb-4 p-4 bg-[#F9F6F0] rounded-lg space-y-3" data-testid="block-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={blockForm.date} onChange={e => setBlockForm({...blockForm, date: e.target.value})} className="input-zuriya" data-testid="block-date-input" />
              </div>
              <div>
                <Label className="text-xs">Reason (optional)</Label>
                <Input value={blockForm.reason} onChange={e => setBlockForm({...blockForm, reason: e.target.value})} placeholder="e.g. Holiday, Personal" className="input-zuriya" data-testid="block-reason-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={blockForm.all_day} onCheckedChange={v => setBlockForm({...blockForm, all_day: v})} />
              <span className="text-sm text-stone-600">Block entire day</span>
            </div>
            {!blockForm.all_day && (
              <div className="flex items-center gap-2">
                <Input type="time" value={blockForm.start_time} onChange={e => setBlockForm({...blockForm, start_time: e.target.value})} className="input-zuriya w-32" />
                <span className="text-sm text-stone-400">to</span>
                <Input type="time" value={blockForm.end_time} onChange={e => setBlockForm({...blockForm, end_time: e.target.value})} className="input-zuriya w-32" />
              </div>
            )}
            <Button onClick={addBlock} className="btn-primary" data-testid="confirm-block-btn">Block Date</Button>
          </div>
        )}

        {blockedDates.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-6">No blocked dates. Your weekly schedule applies.</p>
        ) : (
          <div className="space-y-2">
            {blockedDates.map(block => (
              <div
                key={block.block_id}
                className="flex items-start justify-between gap-3 p-3 bg-red-50 border border-red-100 rounded-lg"
                data-testid={`block-${block.block_id}`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
                  <span className="text-sm font-medium text-[#1A1A1A]">
                    {new Date(block.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {!block.all_day && block.start_time && (
                    <span className="text-xs text-stone-500">{block.start_time} – {block.end_time}</span>
                  )}
                  {block.all_day && <span className="text-xs text-red-500">All day</span>}
                  {block.reason && <span className="text-xs text-stone-400 truncate max-w-full">· {block.reason}</span>}
                </div>
                <button
                  onClick={() => removeBlock(block.block_id)}
                  className="p-1 hover:bg-red-100 rounded shrink-0"
                  data-testid={`remove-block-${block.block_id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
